(() => {
  class FortuneTailCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.panel = this.querySelector('.ft-cart-drawer__panel');
      this.closeButton = this.querySelector('.ft-cart-drawer__close');
      this.content = this.querySelector('[data-cart-drawer-content]');
      this.status = this.querySelector('[data-cart-drawer-status]');
      this.footer = this.querySelector('[data-cart-drawer-footer]');
      this.previouslyFocused = null;
      this.previousBodyPadding = '';
      this.pendingLineKeys = new Set();
      this.announcementTimer = null;
      this.upsellPending = false;
      this.cartBusy = false;
      try {
        this.giftConfig = JSON.parse(this.querySelector('[data-gift-config]')?.textContent || '{}');
      } catch { this.giftConfig = {}; }

      try {
        const upsellData = JSON.parse(this.querySelector('[data-cart-upsell-products]')?.textContent || '[]');
        this.upsellProducts = upsellData.filter(Boolean);
      } catch {
        this.upsellProducts = [];
      }

      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('submit', this.handleProductSubmit);
      document.addEventListener('cart:updated', this.handleCartUpdated);
      document.addEventListener('fortune-tail:cart:updated', this.handleCartUpdated);
      this.addEventListener('click', this.handleDrawerClick);
      this.addEventListener('submit', this.handleCheckoutSubmit);
      try { this.renderCart(JSON.parse(this.querySelector('[data-initial-cart]').textContent)); }
      catch { /* Keep the server-rendered cart if initial data is unavailable. */ }
      this.handleOpenCartRequest();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('submit', this.handleProductSubmit);
      document.removeEventListener('cart:updated', this.handleCartUpdated);
      document.removeEventListener('fortune-tail:cart:updated', this.handleCartUpdated);
      this.removeEventListener('click', this.handleDrawerClick);
      this.removeEventListener('submit', this.handleCheckoutSubmit);
      window.clearTimeout(this.announcementTimer);
      if (this.classList.contains('is-open')) this.unlockPage();
      this.initialized = false;
    }

    handleDocumentClick = (event) => {
      const trigger = event.target.closest('[data-cart-drawer-trigger]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    };

    handleDrawerClick = (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) {
        this.close();
        return;
      }

      if (this.cartBusy) return;
      const giftButton = event.target.closest('[data-gift-claim]');
      if (giftButton) {
        giftButton.dataset.variantId = this.querySelector('[data-gift-colour]')?.value;
        this.addUpsell(giftButton);
        return;
      }
      const upsellButton = event.target.closest('[data-cart-upsell-add]');
      if (upsellButton) {
        this.addUpsell(upsellButton);
        return;
      }

      const action = event.target.closest(
        '[data-cart-line-decrease], [data-cart-line-increase], [data-cart-line-remove]',
      );
      if (!action) return;

      const lineItem = action.closest('[data-cart-line-item]');
      if (!lineItem) return;
      const quantity = Number.parseInt(lineItem.querySelector('[data-cart-line-quantity]')?.textContent, 10) || 1;

      if (action.hasAttribute('data-cart-line-remove')) {
        this.changeLine(lineItem, 0, 'remove');
      } else if (action.hasAttribute('data-cart-line-increase')) {
        this.changeLine(lineItem, quantity + 1, 'increase');
      } else if (quantity > 1) {
        this.changeLine(lineItem, quantity - 1, 'decrease');
      }
    };

    handleCheckoutSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.querySelector('[name="checkout"]')) return;
      this.saveStorefrontReturnUrl();
    };

    saveStorefrontReturnUrl() {
      try {
        const returnUrl = new URL(window.location.href);
        const blockedPath = /^\/(?:checkouts?|account|cart)(?:\/|$)/i;
        if (returnUrl.origin !== window.location.origin || blockedPath.test(returnUrl.pathname)) return;
        returnUrl.searchParams.delete('open_cart');
        window.sessionStorage.setItem('fortune_tail_return_url', returnUrl.href);
      } catch {
        // Checkout must remain available when storage is disabled or unavailable.
      }
    }

    handleOpenCartRequest() {
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.get('open_cart') !== '1') return;

      this.open();
      currentUrl.searchParams.delete('open_cart');
      window.history.replaceState(
        window.history.state,
        '',
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
      );
    }

    async addUpsell(button) {
      if (this.cartBusy || this.upsellPending || button.disabled) return;
      const variantId = Number.parseInt(button.dataset.variantId, 10);
      if (!Number.isFinite(variantId)) return;

      this.upsellPending = true;
      this.setCartBusy(true);
      const isGift = button.hasAttribute('data-gift-claim');
      const previousText = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = button.dataset.loadingLabel || 'Adding…';
      this.announce('');

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1, ...(isGift ? { properties: { _ft_gift: 'scoop-2-bags' } } : {}) }] }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');

        const cart = await this.reconcileGift(await this.fetchCart());
        this.renderCart(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: this } }));
        document.dispatchEvent(new CustomEvent('fortune-tail:cart:updated', { detail: { cart, source: this } }));
        const giftApplied = cart.items.some(item => item.properties?._ft_gift === 'scoop-2-bags' && item.final_line_price === 0);
        this.announce(isGift ? (giftApplied ? this.giftConfig.text.added : this.giftConfig.text.error) : 'Recommended item added to your bag.', isGift && !giftApplied, 3000);
        requestAnimationFrame(() => {
          if (isGift) {
            this.content.scrollTop = 0;
            const giftHeading = this.content.querySelector('.ft-cart-drawer__gift strong');
            if (giftHeading) { giftHeading.tabIndex = -1; giftHeading.focus({ preventScroll: true }); }
          } else {
            const nextUpsellAction = this.content.querySelector('.ft-cart-drawer__upsell-action');
            (nextUpsellAction || this.querySelector('.ft-cart-drawer__checkout'))?.focus();
          }
        });
      } catch (error) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = previousText;
        this.announce(error.message || 'Unable to add this item.', true);
      } finally {
        this.upsellPending = false;
        this.setCartBusy(false);
      }
    }

    handleKeydown = (event) => {
      if (!this.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        this.panel?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    handleProductSubmit = async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !this.isCartAddForm(form) || event.defaultPrevented) return;

      event.preventDefault();
      if (this.cartBusy) { this.announce(this.giftConfig.text?.busy, true); return; }
      this.setCartBusy(true);
      const submitter = event.submitter || form.querySelector('[type="submit"]');
      const buttonState = this.setSubmitting(submitter, true);

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: new FormData(form),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');

        const cart = await this.reconcileGift(await this.fetchCart());
        this.renderCart(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: this } }));
        document.dispatchEvent(new CustomEvent('fortune-tail:cart:updated', { detail: { cart, source: this } }));
        this.open(submitter || form);
        this.announce('Item added to your bag.', false, 1800);
      } catch (error) {
        this.announce(error.message || 'Unable to add this item.', true);
        document.dispatchEvent(new CustomEvent('fortune-tail:cart:error', { detail: { error, form } }));
      } finally {
        this.setSubmitting(submitter, false, buttonState);
        this.setCartBusy(false);
      }
    };

    async changeLine(lineItem, quantity, actionName) {
      const lineKey = lineItem.dataset.lineKey;
      if (!lineKey || this.cartBusy || this.pendingLineKeys.has(lineKey)) return;
      this.setCartBusy(true);

      const rows = [...this.content.querySelectorAll('[data-cart-line-item]')];
      const rowIndex = rows.indexOf(lineItem);
      const productTitle = lineItem.querySelector('.ft-cart-drawer__item-details > a')?.textContent?.trim() || 'Item';
      const previousScrollTop = this.content.scrollTop;
      const controlStates = [...lineItem.querySelectorAll('button')].map((button) => ({
        button,
        disabled: button.disabled,
      }));

      this.pendingLineKeys.add(lineKey);
      lineItem.setAttribute('aria-busy', 'true');
      controlStates.forEach(({ button }) => {
        button.disabled = true;
      });
      this.announce('');

      try {
        const response = await fetch(this.dataset.cartChangeUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: lineKey, quantity }),
        });
        let cart = await response.json();
        if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to update your bag.');
        cart = await this.reconcileGift(cart);

        this.renderCart(cart);
        this.content.scrollTop = previousScrollTop;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: this } }));
        document.dispatchEvent(new CustomEvent('fortune-tail:cart:updated', { detail: { cart, source: this } }));

        if (quantity === 0) {
          this.announce(`${productTitle} removed from your bag.`, false, 2400);
        } else {
          this.announce(`${productTitle} quantity updated to ${quantity}.`, false, 2400);
        }
        this.restoreLineFocus(lineKey, rowIndex, actionName, cart.item_count);
      } catch (error) {
        lineItem.removeAttribute('aria-busy');
        controlStates.forEach(({ button, disabled }) => {
          button.disabled = disabled;
        });
        this.announce(error.message || 'Unable to update your bag.', true);
      } finally {
        this.pendingLineKeys.delete(lineKey);
        this.setCartBusy(false);
      }
    }

    restoreLineFocus(lineKey, previousIndex, actionName, itemCount) {
      requestAnimationFrame(() => {
        if (itemCount === 0) {
          this.querySelector('#FortuneTailCartDrawerTitle')?.focus();
          return;
        }

        const rows = [...this.content.querySelectorAll('[data-cart-line-item]')];
        if (actionName === 'remove') {
          const nextRow = rows[Math.min(previousIndex, rows.length - 1)];
          nextRow?.querySelector('button:not([disabled]), a[href]')?.focus();
          return;
        }

        const updatedRow = rows.find((row) => row.dataset.lineKey === lineKey);
        const selector = actionName === 'increase' ? '[data-cart-line-increase]' : '[data-cart-line-decrease]';
        const preferredControl = updatedRow?.querySelector(selector);
        const fallbackControl = updatedRow?.querySelector('button:not([disabled]), a[href]');
        (preferredControl && !preferredControl.disabled ? preferredControl : fallbackControl)?.focus();
      });
    }

    handleCartUpdated = (event) => {
      if (event.detail?.source === this || !event.detail?.cart) return;
      if (!this.cartBusy) this.refreshCart();
    };

    isCartAddForm(form) {
      try {
        const action = new URL(form.action, window.location.origin);
        return action.pathname.endsWith('/cart/add');
      } catch {
        return false;
      }
    }

    async fetchCart() {
      const response = await fetch(this.dataset.cartJsonUrl, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      if (!response.ok) throw new Error('Unable to refresh your bag.');
      return response.json();
    }

    renderCart(cart) {
      this.updateCartCounts(cart.item_count);
      this.updateCartSubtotal(cart.total_price);
      const cartContent = cart.item_count === 0 ? this.createEmptyState() : this.createCartContents(cart);
      this.content.replaceChildren(this.createGiftOffer(cart), cartContent, this.createUpsell(cart));
      if (this.footer) this.footer.hidden = cart.item_count === 0;
    }

    setCartBusy(busy) {
      this.cartBusy = busy;
      const checkout = this.querySelector('.ft-cart-drawer__checkout');
      if (checkout) { checkout.disabled = busy; checkout.setAttribute('aria-busy', String(busy)); }
    }

    giftActive() {
      const now = Date.now();
      return now >= Date.parse(this.giftConfig.startsAt) && now < Date.parse(this.giftConfig.endsAt);
    }

    giftBagCount(cart) {
      return cart.items.reduce((count, item) => count + (item.selling_plan_allocation ? 0 : (this.giftConfig.bags?.[item.variant_id] || 0) * item.quantity), 0);
    }

    async reconcileGift(cart) {
      // A claimed gift must never silently turn into a paid item.
      const invalid = cart.items.filter(item => item.properties?._ft_gift === 'scoop-2-bags' &&
        (!this.giftActive() || this.giftBagCount(cart) < 2 || item.final_line_price !== 0));
      for (const item of invalid) {
        const response = await fetch(this.dataset.cartChangeUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
        if (!response.ok) throw new Error(this.giftConfig.text.error);
        cart = await response.json();
      }
      return cart;
    }

    async refreshCart() {
      if (this.cartBusy) return;
      this.setCartBusy(true);
      try { this.renderCart(await this.reconcileGift(await this.fetchCart())); }
      catch (error) { this.announce(error.message, true); }
      finally { this.setCartBusy(false); }
    }

    createGiftOffer(cart) {
      const card = document.createElement('section');
      card.className = 'ft-cart-drawer__gift';
      const bags = this.giftBagCount(cart);
      if (!bags || !this.giftActive()) { card.hidden = true; return card; }
      const text = this.giftConfig.text;
      const scoops = cart.items.filter(item => item.product_id === this.giftConfig.productId);
      const freeScoop = scoops.some(item => item.original_line_price > item.final_line_price);
      const heading = document.createElement('strong');
      heading.textContent = freeScoop ? text.added : (bags < 2 ? text.below : text.eligible);
      card.append(heading);
      if (bags < 2 || freeScoop) return card;
      const variants = this.giftConfig.variants.filter(variant => variant.available);
      if (!variants.length) { heading.textContent = text.unavailable; return card; }
      // Existing paid scoops indicate a discount conflict; never add a second paid item.
      if (scoops.length) { heading.textContent = text.error; return card; }
      const controls = document.createElement('div');
      controls.className = 'ft-cart-drawer__gift-controls';
      const select = document.createElement('select');
      select.dataset.giftColour = '';
      select.setAttribute('aria-label', text.choose);
      variants.forEach(variant => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title;
        select.append(option);
      });
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.giftClaim = '';
      button.dataset.loadingLabel = text.adding;
      button.textContent = text.claim;
      controls.append(select, button);
      const note = document.createElement('small');
      note.textContent = text.limit;
      card.append(controls, note);
      return card;
    }

    createEmptyState() {
      const wrapper = document.createElement('div');
      wrapper.className = 'ft-cart-drawer__empty';

      const heading = document.createElement('h3');
      heading.textContent = this.dataset.emptyHeading;
      wrapper.append(heading);

      if (this.dataset.emptyText) {
        const text = document.createElement('p');
        text.textContent = this.dataset.emptyText;
        wrapper.append(text);
      }

      const link = document.createElement('a');
      link.className = 'ft-cart-drawer__continue';
      link.href = this.dataset.continueUrl;
      link.textContent = this.dataset.emptyButton;
      wrapper.append(link);
      return wrapper;
    }

    createCartContents(cart) {
      const wrapper = document.createElement('div');
      wrapper.className = 'ft-cart-drawer__contents';
      const list = document.createElement('ul');
      list.className = 'ft-cart-drawer__items';
      list.setAttribute('role', 'list');

      cart.items.forEach((item) => {
        const row = document.createElement('li');
        row.className = 'ft-cart-drawer__item';
        row.dataset.cartLineItem = '';
        row.dataset.lineKey = item.key;

        const remove = document.createElement('button');
        remove.className = 'ft-cart-drawer__remove';
        remove.type = 'button';
        remove.dataset.cartLineRemove = '';
        remove.setAttribute('aria-label', `Remove ${item.product_title} from cart`);
        remove.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 7h16M9 3h6l1 4H8l1-4Zm-2 4 1 14h8l1-14M10 11v6M14 11v6"></path></svg>';
        row.append(remove);

        if (item.image) {
          const imageLink = document.createElement('a');
          imageLink.className = 'ft-cart-drawer__item-image';
          imageLink.href = item.url;
          imageLink.tabIndex = -1;
          imageLink.setAttribute('aria-hidden', 'true');
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.width = 76;
          image.height = 86;
          image.loading = 'lazy';
          imageLink.append(image);
          row.append(imageLink);
        }

        const details = document.createElement('div');
        details.className = 'ft-cart-drawer__item-details';
        const title = document.createElement('a');
        title.href = item.url;
        title.textContent = item.product_title;
        let variant = null;
        if (item.variant_title && item.variant_title !== 'Default Title') {
          variant = document.createElement('span');
          variant.className = 'ft-cart-drawer__variant';
          variant.textContent = item.variant_title;
        }

        const price = document.createElement('strong');
        price.className = 'ft-cart-drawer__line-price';
        if (item.original_line_price > item.final_line_price) {
          const original = document.createElement('s');
          original.textContent = this.formatMoney(item.original_line_price);
          price.append(original, document.createTextNode(' '));
        }
        price.append(document.createTextNode(item.final_line_price === 0 ? this.giftConfig.text.free : this.formatMoney(item.final_line_price)));
        const quantity = this.createQuantityControl(item);
        const actions = document.createElement('div');
        actions.className = 'ft-cart-drawer__item-actions';
        actions.append(quantity, price);
        details.append(title);
        if (variant) details.append(variant);
        details.append(actions);
        row.append(details);
        list.append(row);
      });

      wrapper.append(list);
      return wrapper;
    }

    createUpsell(cart) {
      const section = document.createElement('section');
      section.className = 'ft-cart-drawer__upsell';
      section.dataset.cartUpsell = '';
      const heading = document.createElement('h3');
      heading.textContent = this.dataset.upsellHeading;
      const list = document.createElement('div');
      list.className = 'ft-cart-drawer__upsell-list';

      this.upsellProducts.filter(product => !(this.giftActive() && product.handle === 'cat-litter-scoop')).forEach((product) => {
        list.append(this.createUpsellCard(product));
      });

      section.append(heading, list);
      return section;
    }

    createUpsellCard(product) {
      const card = document.createElement('article');
      card.className = 'ft-cart-drawer__upsell-card';

      if (product.image) {
        const imageLink = document.createElement('a');
        imageLink.className = 'ft-cart-drawer__upsell-image';
        imageLink.href = product.url;
        imageLink.tabIndex = -1;
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.image_alt || product.title;
        image.width = 64;
        image.height = 70;
        image.loading = 'lazy';
        imageLink.append(image);
        card.append(imageLink);
      }

      const details = document.createElement('div');
      details.className = 'ft-cart-drawer__upsell-details';
      const title = document.createElement('a');
      title.href = product.url;
      title.textContent = product.title;
      const price = document.createElement('strong');
      price.textContent = this.formatMoney(product.price);
      details.append(title, price);
      card.append(details);

      if (product.variant_count === 1) {
        const addButton = document.createElement('button');
        addButton.className = 'ft-cart-drawer__upsell-action';
        addButton.type = 'button';
        addButton.setAttribute('aria-label', `Add ${product.title} to cart`);
        addButton.dataset.cartUpsellAdd = '';
        addButton.dataset.variantId = product.variant_id;
        addButton.dataset.loadingLabel = 'Adding…';
        addButton.disabled = !product.variant_available;
        addButton.textContent = product.variant_available ? 'Add' : 'Sold Out';
        card.append(addButton);
      } else {
        const optionsLink = document.createElement('a');
        optionsLink.className = 'ft-cart-drawer__upsell-action';
        optionsLink.href = product.url;
        optionsLink.textContent = 'Choose Options';
        card.append(optionsLink);
      }

      return card;
    }

    createQuantityControl(item) {
      if (item.properties?._ft_gift === 'scoop-2-bags') {
        const fixed = document.createElement('span');
        fixed.dataset.cartLineQuantity = '';
        fixed.textContent = item.quantity;
        return fixed;
      }
      const quantity = document.createElement('div');
      quantity.className = 'ft-cart-drawer__quantity';

      const decrease = document.createElement('button');
      decrease.type = 'button';
      decrease.dataset.cartLineDecrease = '';
      decrease.disabled = item.quantity <= 1;
      decrease.setAttribute('aria-label', `Decrease quantity for ${item.product_title}`);
      decrease.innerHTML = '<span aria-hidden="true">−</span>';

      const value = document.createElement('span');
      value.className = 'ft-cart-drawer__quantity-value';
      value.dataset.cartLineQuantity = '';
      value.textContent = item.quantity;
      value.setAttribute('aria-label', `Quantity for ${item.product_title}: ${item.quantity}`);

      const increase = document.createElement('button');
      increase.type = 'button';
      increase.dataset.cartLineIncrease = '';
      increase.setAttribute('aria-label', `Increase quantity for ${item.product_title}`);
      increase.innerHTML = '<span aria-hidden="true">+</span>';

      quantity.append(decrease, value, increase);
      return quantity;
    }

    updateCartCounts(count) {
      document.querySelectorAll('.ft-header__cart-count').forEach((element) => {
        element.textContent = count;
      });
      const titleCount = this.querySelector('[data-cart-drawer-title-count]');
      if (titleCount) titleCount.textContent = `(${count})`;
    }

    updateCartSubtotal(totalPrice) {
      document.querySelectorAll('[data-cart-subtotal]').forEach((element) => {
        element.textContent = this.formatMoney(totalPrice);
      });
    }

    formatMoney(cents, moneyFormat = this.dataset.moneyFormat) {
      const format = moneyFormat || '{{amount}}';
      const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match) return format;

      const formats = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
      };
      const [precision, thousands, decimal] = formats[match[1]] || formats.amount;
      const value = (cents / 100).toFixed(precision);
      const parts = value.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const formatted = precision > 0 ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      return format.replace(match[0], formatted);
    }

    setSubmitting(button, submitting, previousState = null) {
      if (!(button instanceof HTMLButtonElement)) return previousState;
      const label = button.querySelector('[data-add-to-cart-text]') || button;
      if (submitting) {
        const state = {
          disabled: button.disabled,
          text: label.textContent,
        };
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        label.textContent = button.dataset.loadingLabel || 'Adding…';
        return state;
      }
      button.disabled = previousState?.disabled || false;
      button.removeAttribute('aria-busy');
      if (previousState?.text) label.textContent = previousState.text;
      return previousState;
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.previouslyFocused = opener instanceof HTMLElement ? opener : document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.lockPage();
      if (!this.cartBusy) this.refreshCart();
      requestAnimationFrame(() => this.closeButton?.focus());
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.unlockPage();
      if (this.previouslyFocused?.isConnected) this.previouslyFocused.focus();
      this.previouslyFocused = null;
    }

    lockPage() {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      this.previousBodyPadding = document.body.style.paddingRight;
      if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.body.classList.add('ft-cart-drawer-open');
    }

    unlockPage() {
      document.body.classList.remove('ft-cart-drawer-open');
      document.body.style.paddingRight = this.previousBodyPadding;
    }

    getFocusableElements() {
      return [...this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getClientRects().length > 0);
    }

    announce(message, isError = false, clearAfter = 0) {
      if (!this.status) return;
      window.clearTimeout(this.announcementTimer);
      this.status.classList.toggle('is-error', isError);
      this.status.textContent = '';
      if (!message) return;
      requestAnimationFrame(() => {
        this.status.textContent = message;
      });
      if (clearAfter > 0) {
        this.announcementTimer = window.setTimeout(() => {
          this.status.textContent = '';
        }, clearAfter);
      }
    }
  }

  if (!customElements.get('fortune-tail-cart-drawer')) {
    customElements.define('fortune-tail-cart-drawer', FortuneTailCartDrawer);
  }
})();
